import { parseDateFromInput, convertToApiFormat } from "./dateParser";

export interface DateDetectionCallbacks {
  setPatientDOB: (value: string) => void;
  setInputText: (value: string) => void;
  setQueryText?: (value: string) => void;
  setSearchText?: (value: string) => void;
}

// Type guard to check if debouncedSearch has a cancel method
function hasCancelMethod(
  fn: ((value: string) => void) | undefined
): fn is { (value: string): void; cancel: () => void } {
  return fn !== undefined && typeof (fn as any).cancel === "function";
}

export interface DateDetectionOptions {
  minSearchLength?: number;
  debouncedSearch?: ((value: string) => void) & { cancel?: () => void };
  isValidDateFormat?: (value: string) => boolean;
  currentPatientDOB?: string;
  currentValue?: string;
}

/**
 * Core date detection logic that can be shared between React hooks and service classes
 * Handles date parsing, formatting, and query text management
 * 
 * @param value - The input value to check for dates
 * @param callbacks - Object containing state setter functions
 * @param options - Optional configuration for search length, debounced search, etc.
 */
export function processDateDetection(
  value: string,
  callbacks: DateDetectionCallbacks,
  options: DateDetectionOptions = {}
): void {
  const {
    minSearchLength = 3,
    debouncedSearch,
    isValidDateFormat,
    currentPatientDOB,
    currentValue,
  } = options;

  // Try to detect and parse date from input
  const dateResult = parseDateFromInput(value);

  if (dateResult.isDate && dateResult.date) {
    // Date detected - set patientDOB and remove date from query
    const apiFormattedDate = convertToApiFormat(dateResult.date);
    callbacks.setPatientDOB(apiFormattedDate);

    // Update input text to show the formatted date
    const displayDate = dateResult.date.replace(/\//g, "-");
    callbacks.setInputText(displayDate);

    // If there's remaining text, use it as query; otherwise clear query completely
    const remainingQuery = (dateResult.remainingText || "").trim();
    
    // Use setQueryText if available (for TestOrdersFilterService), otherwise use setSearchText
    if (callbacks.setQueryText !== undefined) {
      callbacks.setQueryText(remainingQuery);
      
      // Trigger debounced search for remaining text if any
      if (remainingQuery && remainingQuery.length >= minSearchLength) {
        if (debouncedSearch) {
          debouncedSearch(remainingQuery);
        }
      } else {
        // No remaining text or too short - clear query to ensure it's not sent in header
        if (debouncedSearch) {
          debouncedSearch("");
        }
      }
    } else {
      // For React components using setSearchText
      if (remainingQuery) {
        if (callbacks.setSearchText) {
          callbacks.setSearchText(remainingQuery);
        }
        if (debouncedSearch) {
          debouncedSearch(remainingQuery);
        }
      } else {
        // No remaining text - clear query to ensure it's not sent in header
        if (callbacks.setSearchText) {
          callbacks.setSearchText("");
        }
        if (hasCancelMethod(debouncedSearch)) {
          debouncedSearch.cancel();
        }
      }
    }
  } else {
    // No date detected - treat as regular text search
    // Clear patientDOB if it was set
    if (currentPatientDOB) {
      callbacks.setPatientDOB("");
    }

    // Special handling for TestOrdersFilterService: Don't send as query if the value is a date format (MM-DD-YYYY) and matches patientDOB
    // This means date was selected from calendar, so only patientDOB should be sent
    if (
      isValidDateFormat &&
      currentPatientDOB &&
      currentValue &&
      isValidDateFormat(currentValue) &&
      currentPatientDOB === currentValue
    ) {
      // Date selected from calendar, don't send as query parameter
      if (callbacks.setQueryText !== undefined) {
        callbacks.setQueryText("");
      }
      return;
    }

    // Trigger debounced search for text queries
    if (callbacks.setQueryText !== undefined) {
      // For TestOrdersFilterService
      if (value === "") {
        // Clear queryText immediately when input is cleared
        callbacks.setQueryText("");
        if (debouncedSearch) {
          debouncedSearch("");
        }
      } else if (value.length >= minSearchLength) {
        // For valid search queries, use debounced search to avoid too many API calls
        if (debouncedSearch) {
          debouncedSearch(value);
        } else {
          // Fallback: set queryText directly if debouncedSearch is not initialized
          // This ensures search works even if debouncing is not set up
          callbacks.setQueryText(value);
        }
      } else {
        // For short queries (< minSearchLength), clear queryText to prevent searching
        callbacks.setQueryText("");
        if (hasCancelMethod(debouncedSearch)) {
          debouncedSearch.cancel();
        }
      }
    } else {
      // For React components
      if (debouncedSearch) {
        debouncedSearch(value);
      }
    }
  }
}

